/*
INTELLECTUAL PROPERTY NOTICE
© Orbit Tickets Ltd. All rights reserved.
This file contains Custom / Included Intellectual Property created for and owned by the Customer as part of an application developed by Kaleida within the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, the inclusion of any application within the Platform does not restrict the Customer's right to use Custom / Included Intellectual Property outside of the Platform.
*/

// IP-NOTICE: CUSTOM
import {
	AfterContentInit, ChangeDetectionStrategy, Component, ContentChildren, EventEmitter, Input, OnChanges, Output,
	QueryList, SimpleChanges,
} from '@angular/core';
import { MatPaginatorSelectConfig, PageEvent } from '@angular/material/paginator';
import { Sort, SortDirection } from '@angular/material/sort';
import {
	OrbitTableActionConfig, OrbitTableColumnConfig, OrbitTableConfig, OrbitTableListActionEvent, OrbitTablePageEvent,
	OrbitTableRowActionEvent, OrbitTableSelectionEvent, OrbitTableSortEvent,
} from './orbit-table.models';
import { OrbitTableCellTemplateDirective, OrbitTableHeaderTemplateDirective } from './orbit-table-template.directive';

@Component({
	selector: 'orbit-table',
	templateUrl: './orbit-table.component.html',
	styleUrls: ['./orbit-table.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class OrbitTableComponent<T> implements AfterContentInit, OnChanges {
	@Input({ required: true }) config!: OrbitTableConfig<T>;
	@Input() rows: readonly T[] = [];
	@Input() resultCount: number | null = null;
	@Input() totalCount: number | null = null;
	@Input() loading = false;
	@Input() error = false;
	@Input() selectedRowId: string | number | null = null;

	@Output() selectedRowChange = new EventEmitter<OrbitTableSelectionEvent<T>>();
	@Output() rowClick = new EventEmitter<T>();
	@Output() rowDoubleClick = new EventEmitter<T>();
	@Output() rowAction = new EventEmitter<OrbitTableRowActionEvent<T>>();
	@Output() listAction = new EventEmitter<OrbitTableListActionEvent>();
	@Output() sortChange = new EventEmitter<OrbitTableSortEvent>();
	@Output() pageChange = new EventEmitter<OrbitTablePageEvent>();

	@ContentChildren(OrbitTableCellTemplateDirective) cellTemplates!: QueryList<OrbitTableCellTemplateDirective>;
	@ContentChildren(OrbitTableHeaderTemplateDirective) headerTemplates!: QueryList<OrbitTableHeaderTemplateDirective>;

	private cellTemplateMap = new Map<string, OrbitTableCellTemplateDirective>();
	private headerTemplateMap = new Map<string, OrbitTableHeaderTemplateDirective>();
	pageIndex = 0;
	currentPageSize = 20;
	readonly paginatorSelectConfig: MatPaginatorSelectConfig = { panelClass: 'orbit-paginator-select-panel' };
	sortActive = '';
	sortDirection: SortDirection = '';

	ngAfterContentInit(): void {
		this.rebuildTemplateMaps();
		this.cellTemplates.changes.subscribe(() => this.rebuildTemplateMaps());
		this.headerTemplates.changes.subscribe(() => this.rebuildTemplateMaps());
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.config) {
			const previous = changes.config.previousValue as OrbitTableConfig<T> | undefined;
			if (changes.config.firstChange || previous?.sorting?.active !== this.config?.sorting?.active || previous?.sorting?.direction !== this.config?.sorting?.direction) {
				this.sortActive = this.config?.sorting?.active ?? '';
				this.sortDirection = this.config?.sorting?.direction ?? '';
			}
			if (changes.config.firstChange || previous?.pagination?.pageSize !== this.config?.pagination?.pageSize || previous?.pagination?.pageIndex !== this.config?.pagination?.pageIndex) {
				this.currentPageSize = this.config?.pagination?.pageSize ?? 20;
				this.pageIndex = this.config?.pagination?.pageIndex ?? 0;
			}
		}
		if (changes.rows && !changes.rows.firstChange) this.ensureValidPage();
	}

	get displayedColumns(): string[] {
		return [...(this.config?.columns ?? []).map(column => column.key), ...(this.config?.rowActions?.length ? ['__actions'] : [])];
	}

	get displayedRows(): readonly T[] {
		let result = [...(this.rows ?? [])];
		if (this.config?.sorting?.enabled && this.config.sorting.mode !== 'server' && this.sortActive && this.sortDirection) {
			const column = this.config.columns.find(item => (item.sortField ?? item.key) === this.sortActive || item.key === this.sortActive);
			if (column) result.sort((left, right) => this.compare(this.columnValue(column, left), this.columnValue(column, right)) * (this.sortDirection === 'asc' ? 1 : -1));
		}
		if (this.config?.pagination?.enabled && this.config.pagination.mode !== 'server') {
			const size = this.pageSize;
			result = result.slice(this.pageIndex * size, this.pageIndex * size + size);
		}
		return result;
	}

	get pageSize(): number { return this.currentPageSize; }
	get paginatorLength(): number { return this.config?.pagination?.mode === 'server' ? (this.resultCount ?? this.rows.length) : this.rows.length; }
	get currentResultCount(): number { return this.resultCount ?? this.rows.length; }

	get resultEntityLabel(): string {
		const summary = this.config?.resultSummary;
		if (!summary) return '';
		return this.currentResultCount === 1 ? summary.businessObjectName : summary.businessObjectNamePlural;
	}

	columnValue(column: OrbitTableColumnConfig<T>, row: T): unknown {
		if (column.value) return column.value(row);
		const path = String(column.property ?? column.key).split('.');
		return path.reduce<unknown>((value, key) => value == null ? value : (value as Record<string, unknown>)[key], row);
	}

	displayValue(column: OrbitTableColumnConfig<T>, row: T): string {
		const value = this.columnValue(column, row);
		if (column.format) return column.format(value, row);
		return value == null ? '' : String(value);
	}

	cellTemplate(columnKey: string) { return this.cellTemplateMap.get(columnKey)?.template ?? null; }
	headerTemplate(columnKey: string) { return this.headerTemplateMap.get(columnKey)?.template ?? null; }

	isSortable(column: OrbitTableColumnConfig<T>): boolean { return !!this.config?.sorting?.enabled && column.sortable !== false; }
	isSticky(column: OrbitTableColumnConfig<T>, index: number): boolean {
		return !!column.sticky || (this.config?.scroll?.stickyFirstColumn !== false && index === 0);
	}

	onSortChanged(sort: Sort): void {
		this.sortActive = sort.active;
		this.sortDirection = sort.direction;
		this.pageIndex = 0;
		this.sortChange.emit(sort);
	}

	onPageChanged(page: PageEvent): void {
		this.pageIndex = page.pageIndex;
		this.currentPageSize = page.pageSize;
		this.pageChange.emit(page);
	}

	onRowClicked(row: T, event: MouseEvent): void {
		if (this.isInteractiveEvent(event)) return;
		this.rowClick.emit(row);
		if (this.config?.selection?.mode === 'single' && this.config.selection.selectOnRowClick !== false) this.selectRow(row);
	}

	onRowDoubleClicked(row: T, event: MouseEvent): void {
		if (!this.isInteractiveEvent(event)) this.rowDoubleClick.emit(row);
	}

	onRowKeydown(row: T, event: KeyboardEvent): void {
		if (this.config?.selection?.mode !== 'single' || this.isInteractiveEvent(event) || (event.key !== 'Enter' && event.key !== ' ')) return;
		event.preventDefault();
		this.selectRow(row);
	}

	selectRow(row: T): void {
		const rowId = this.rowId(row);
		this.selectedRowId = rowId;
		this.selectedRowChange.emit({ row, rowId });
	}

	isSelected(row: T): boolean { return this.config?.selection?.mode === 'single' && this.selectedRowId === this.rowId(row); }
	isSelectable(): boolean { return this.config?.selection?.mode === 'single'; }
	rowId(row: T): string | number { return this.config.selection?.rowId(row) ?? String(this.rows.indexOf(row)); }

	invokeRowAction(action: OrbitTableActionConfig<T>, row: T, event: Event): void {
		event.stopPropagation();
		if (!this.actionDisabled(action, row)) this.rowAction.emit({ actionId: action.id, row });
	}

	invokeListAction(action: OrbitTableActionConfig<void>, event: Event): void {
		event.stopPropagation();
		if (!this.actionDisabled(action, undefined)) this.listAction.emit({ actionId: action.id });
	}

	actionVisible(action: OrbitTableActionConfig<any>, row?: T): boolean {
		return typeof action.visible === 'function' ? action.visible(row) : action.visible !== false;
	}

	actionDisabled(action: OrbitTableActionConfig<any>, row?: T): boolean {
		return typeof action.disabled === 'function' ? action.disabled(row) : action.disabled === true;
	}

	actionAriaLabel(action: OrbitTableActionConfig<any>, row?: T): string {
		return typeof action.ariaLabel === 'function' ? action.ariaLabel(row) : action.ariaLabel ?? action.label;
	}

	actionTooltip(action: OrbitTableActionConfig<any>, row?: T): string {
		return typeof action.tooltip === 'function' ? action.tooltip(row) : action.tooltip ?? action.label;
	}

	tooltipFor(column: OrbitTableColumnConfig<T>, row: T): string {
		if (typeof column.tooltip === 'function') return column.tooltip(row) ?? '';
		return column.tooltip ? this.displayValue(column, row) : '';
	}

	cellClass(column: OrbitTableColumnConfig<T>, row: T): string {
		return typeof column.cellClass === 'function' ? column.cellClass(row) : column.cellClass ?? '';
	}

	rowClasses(row: T): Record<string, boolean> {
		const configured = typeof this.config.rowClass === 'function' ? this.config.rowClass(row) : this.config.rowClass ?? '';
		return {
			'orbit-table__row': true,
			'orbit-table__row--hoverable': this.config.rowHover !== false,
			'orbit-table__row--selectable': this.isSelectable(),
			'orbit-table__row--selected': this.isSelected(row),
			'orbit-table__row--selected-actions-visible': this.isSelected(row) && this.config.showRowActionsOnSelected !== false,
			[configured]: !!configured,
		};
	}

	trackByColumn(_: number, column: OrbitTableColumnConfig<T>): string { return column.key; }
	trackByRow = (index: number, row: T): string | number => this.config?.selection?.rowId ? this.rowId(row) : index;
	trackByAction(_: number, action: OrbitTableActionConfig<any>): string { return action.id; }

	private rebuildTemplateMaps(): void {
		this.cellTemplateMap = new Map((this.cellTemplates?.toArray() ?? []).map(item => [item.columnKey, item]));
		this.headerTemplateMap = new Map((this.headerTemplates?.toArray() ?? []).map(item => [item.columnKey, item]));
	}

	private ensureValidPage(): void {
		if (!this.config?.pagination?.enabled || this.config.pagination.mode === 'server') return;
		const lastPage = Math.max(0, Math.ceil(this.rows.length / this.pageSize) - 1);
		this.pageIndex = Math.min(this.pageIndex, lastPage);
	}

	private compare(left: unknown, right: unknown): number {
		if (Object.is(left, right)) return 0;
		if (left == null) return -1;
		if (right == null) return 1;
		if (typeof left === 'number' && typeof right === 'number') return left - right;
		return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
	}

	private isInteractiveEvent(event: Event): boolean {
		const target = event.target as HTMLElement | null;
		return !!target?.closest('button, a, input, select, textarea, [role="button"], [contenteditable="true"], [data-orbit-table-interactive]');
	}
}
